// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import updateAutoDeliveryParameter from "@guestsupply/App/Handlers/AutoDelivery/UpdateAutoDeliveryParameterHandler";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import {
    AutoDeliveryPageContext,
    getAutoDeliveryOrdersDataView,
} from "@guestsupply/App/Store/Selectors/AutoDeliverySelectors";
import { getBackorders, getBackordersParameter } from "@guestsupply/App/Store/Selectors/BackorderSelectors";
import Pagination, { PaginationPresentationProps } from "@guestsupply/MobiusOverrides/Pagination/Pagination";
import TextField from "@guestsupply/MobiusOverrides/TextField/TextField";
import { pluralize } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = { updateAutoDeliveryParameter };

type Props = ResolveThunks<typeof mapDispatchToProps>;

export interface AutoDeliveryPaginationStyles {
    container?: InjectableCss;
    pagination?: PaginationPresentationProps;
    paginationWrapper?: InjectableCss;
}

// XNG-Changes: please check AutoDeliveryPaginationExtensions for styles
export const autoDeliveryPaginationStyles: AutoDeliveryPaginationStyles = {};

const styles = autoDeliveryPaginationStyles;

const AutoDeliveryPagination = ({ updateAutoDeliveryParameter }: Props) => {
    const autoDeliveryDataView = React.useContext(AutoDeliveryPageContext);
    const setCurrentPage = (newPageIndex: number) => {
        if (newPageIndex !== pagination?.page) {
            updateAutoDeliveryParameter({
                page: newPageIndex,
            });
        }
    };

    const setResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => {
        if (resultsPerPage !== pagination?.pageSize) {
            updateAutoDeliveryParameter({
                pageSize: resultsPerPage,
            });
        }
    };

    const { pagination } = autoDeliveryDataView as any;

    if (!autoDeliveryDataView.value || !autoDeliveryDataView.value.length) {
        return null;
    }
    if (!pagination || pagination.totalItemCount === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="AutoDeliveryTablePagination">
            <Pagination
                {...styles.pagination}
                resultsCount={pagination?.totalItemCount || 0}
                currentPage={pagination?.page || 1}
                resultsPerPage={pagination?.pageSize || 8}
                resultsPerPageOptions={[...Array(4).keys()].map(
                    (_, index) => pagination?.defaultPageSize * (index + 1),
                )}
                onChangePage={setCurrentPage}
                onChangeResultsPerPage={setResultsPerPage}
                prevPage={pagination.prevPageUri}
                nextPage={pagination.nextPageUri}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(AutoDeliveryPagination),
    definition: {
        group: "Auto Delivery" as any,
        allowedContexts: ["AutoDeliveryPage"],
    },
};

export default widgetModule;
